import { FC, memo } from 'react'

import { GetServerSideProps } from '../redux/types'
import { ResponseLocals, PageProps } from '../common/types'
import { getMessagesFromLanguage } from '../i18n/utils'
import { getRoutesFromDomain } from '../routes/utils'
import ObjectOverview from '../components/object-overview'
import ObjectOverviewPageTransport, {
    ObjectOverviewParamsTransport,
} from '../transports/object-overview-page-transport'
import { deleteSession } from '../utils/delete-session'
import Page from '../components/common/page'
import { pageClient } from '../clients'
import { BaseTransport } from '../transports'
import { processObjectOverviewTemplates } from '../utils/template-processor'

type Props = {
    objectOverviewLink: string
    data: BaseTransport<ObjectOverviewPageTransport>
} & PageProps

const ObjectOverviewPage: FC<Props> = ({ objectOverviewLink, data }) => {
    const { data: objectOverviewPageTransport } = data

    if (!objectOverviewPageTransport) {
        return null
    }

    const { head, pageHead, heading, subheading, staticData, objectsSection } = objectOverviewPageTransport
    const { navigationSection, newsLetterSection, footerSection, banners } = staticData

    return (
        <Page head={head} pageHead={pageHead}>
            <ObjectOverview
                objectOverviewLink={objectOverviewLink}
                navigationSection={navigationSection}
                newsLetterSection={newsLetterSection}
                footerSection={footerSection}
                banner={banners?.slot1 ?? null}
                footerLogo={navigationSection.navigation.logo}
                socialMediaLinks={navigationSection.socialMediaLinks}
                objects={objectsSection?.objects || []}
                heading={heading}
                subheading={subheading}
                ownerName={objectsSection?.ownerName ?? null}
                totalCount={objectsSection?.totalCount}
                currentPage={objectsSection?.currentPage}
                totalPages={objectsSection?.totalPages}
            />
        </Page>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async ({ req, res, query }) => {
    const { user, device } = res.locals as ResponseLocals

    const { objectOverviewLink, page: pageFromParams } = req.params as unknown as ObjectOverviewParamsTransport

    const pageFromQuery = query?.page as string | undefined
    const page = pageFromParams || pageFromQuery

    const linkParam = objectOverviewLink

    const sitePathname = req.originalUrl || req.path

    const baseObjectOverviewPageTransport = await pageClient.getObjectOverviewPage({
        user,
        params: { objectOverviewLink: linkParam, page, requestPath: sitePathname },
    })

    if (!baseObjectOverviewPageTransport.data || baseObjectOverviewPageTransport.status === 404) {
        return { notFound: true }
    }

    const { objectsSection } = baseObjectOverviewPageTransport.data
    if (objectsSection) {
        const vars = {
            ...(objectsSection.ownerName ? { name: objectsSection.ownerName } : {}),
            count: String(objectsSection.totalCount ?? 0),
            region: objectsSection.objects?.[0]?.seoRegion?.name ?? '',
        }

        processObjectOverviewTemplates(baseObjectOverviewPageTransport.data, vars)
    }

    const requestUserConfig = deleteSession(user)

    const props: any = {
        objectOverviewLink: linkParam,
        data: baseObjectOverviewPageTransport,
        device,
        requestUserConfig: requestUserConfig,
        messages: await getMessagesFromLanguage(user.language),
        routes: await getRoutesFromDomain(user.domain),
        originalPath: req.originalPath,
        originalQuery: req.originalQuery,
        originalUrl: req.originalUrl,
    }

    if (page) {
        props.page = page
    }

    return { props }
}

export default memo(ObjectOverviewPage)
